import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import logger from '../utils/logger';

// Local image cache to store temporary copies of uploaded images
// This will be populated when items are created
const localImageCache = {};

const OfflineImageSimple = ({ 
  src, 
  itemId, 
  alt = '', 
  fallbackSrc = '/placeholder.png', 
  sx = {}, 
  imgProps = {},
  prefetch, // Extract prefetch prop to prevent it from being passed to DOM (legacy compatibility)
  ...rest
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  logger.debug('OfflineImageSimple', `Component ${itemId} render: loading=${loading}, error=${error}, src=${src}`);

  useEffect(() => {
    if (!src) {
      logger.warn('OfflineImageSimple', `No src provided for item ${itemId}`);
      setError(true);
      setLoading(false);
      return;
    }

    // Step 1: Check if we have this image in our local cache (from fresh uploads)
    // The cache stores {dataUrl, src} - only use if src matches to handle image URL changes
    const cachedData = localImageCache[itemId];
    if (cachedData) {
      // Handle both old format (string) and new format ({dataUrl, src})
      const cachedDataUrl = typeof cachedData === 'string' ? cachedData : cachedData.dataUrl;
      const cachedSrc = typeof cachedData === 'string' ? null : cachedData.src;
      
      // Only use cache if no src was stored (legacy) or src matches current
      if (!cachedSrc || cachedSrc === src) {
        logger.debug('OfflineImageSimple', `Using cached local image data for item ${itemId}`);
        setImageSrc(cachedDataUrl);
        setLoading(false);
        setError(false);
        return;
      } else {
        // Image URL changed (e.g., from group photo to cropped image), clear old cache
        logger.debug('OfflineImageSimple', `Image URL changed for item ${itemId}, clearing old cache`);
        delete localImageCache[itemId];
      }
    }
    
    // Step 2: Try to load from server
    logger.debug('OfflineImageSimple', `Loading image from server for item ${itemId}: ${src}`);
    
    // Use the image URL as-is (cache-busting is now handled by smart cache-busting in imageUtils)
    setImageSrc(src);
    setLoading(false);
    setError(false);
    
  }, [src, itemId]);

  // Handle image load error
  const handleError = () => {
    logger.warn('OfflineImageSimple', `Image failed to load for item ${itemId}: ${src}`);
    setError(true);
    setImageSrc(fallbackSrc);
  };

  const handleLoad = () => {
    logger.debug('OfflineImageSimple', `Image loaded successfully for item ${itemId}`);
    setError(false);
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        {...rest}
        sx={{ 
          minHeight: '100px',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          ...sx
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={alt}
      crossOrigin="anonymous"
      loading="lazy"
      decoding="async"
      onError={handleError}
      onLoad={handleLoad}
      {...imgProps}
      {...rest}
      sx={{
        objectFit: 'contain',
        ...sx
      }}
    />
  );
};

/**
 * Utility function to cache a local image for immediate display
 * @param {string|number} itemId - ID of the item to cache image for
 * @param {string} dataUrl - Data URL of the image (from FileReader or canvas)
 * @param {string} src - Optional source URL to associate with this cache entry
 */
export const cacheLocalImage = (itemId, dataUrl, src = null) => {
  if (!itemId || !dataUrl) return;
  
  logger.debug('OfflineImageSimple', `Caching local image for item ${itemId}`);
  localImageCache[itemId] = { dataUrl, src };
};

/**
 * Utility function to clear cached local image
 * @param {string|number} itemId - ID of the item to clear cache for
 */
export const clearLocalImageCache = (itemId) => {
  if (itemId && localImageCache[itemId]) {
    delete localImageCache[itemId];
    logger.debug('OfflineImageSimple', `Cleared local image cache for item ${itemId}`);
    return true;
  } else if (!itemId) {
    // Clear all cached images
    Object.keys(localImageCache).forEach(key => delete localImageCache[key]);
    logger.debug('OfflineImageSimple', 'Cleared all local image caches');
    return true;
  }
  return false;
};

/**
 * Utility function to get a data URL from a file input
 * This can be used to cache the image before upload
 * @param {File} file - File object from input
 * @returns {Promise<string>} Promise that resolves to data URL
 */
export const getDataUrlFromFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default OfflineImageSimple;
